import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import PlanDetailDialog from '../../components/Dialogs/PlanDetailDialog';

interface CoverageItem {
  name: string;
  description?: string;
  type: string;
  coverage_amount?: number;
  copayment_amount?: number;
  coverage_times_per_month?: number;
}

export interface InsurancePlan {
  id: string;
  name: string;
  payor: string;
  description: string;
  planDetails?: {
    free_consultations?: number;
    free_consultations_per_year?: number;
    free_consultations_per_month?: number;
    free_consultations_per_week?: number;
    coverage?: CoverageItem[];
  };
  createdAt: string;
  updatedAt: string;
}

interface PlanDetailDialogContextType {
  openDialog: (plan: InsurancePlan) => void;
  closeDialog: () => void;
}

const PlanDetailDialogContext = createContext<PlanDetailDialogContextType | undefined>(undefined);

export const usePlanDetailDialog = (): PlanDetailDialogContextType => {
  const context = useContext(PlanDetailDialogContext);
  if (!context) {
    throw new Error('usePlanDetailDialog must be used within PlanDetailDialogProvider');
  }
  return context;
};

interface PlanDetailDialogProviderProps {
  children: ReactNode;
}

export const PlanDetailDialogProvider: React.FC<PlanDetailDialogProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [plan, setPlan] = useState<InsurancePlan | null>(null);

  const openDialog = useCallback((planData: InsurancePlan) => {
    setPlan(planData);
    setIsVisible(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsVisible(false);
    setPlan(null);
  }, []);

  return (
    <PlanDetailDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      <PlanDetailDialog
        visible={isVisible}
        plan={plan}
        onClose={closeDialog}
      />
    </PlanDetailDialogContext.Provider>
  );
};

